// `checkCanSendMessage` / `checkIsMessageDataValid`
// (`extension-alert-entity-messages-controller.ng1.ts:487-500`), ported.
//
// ⚠️ SEND IS THE FURTHEST-REACHING CONTROL IN THIS APP — it puts mail in a candidate's inbox,
// and unlike an ATS record it cannot be withdrawn. Every one of these conjuncts is a reason
// the button must stay dead, and dropping any of them enables Send in a state upstream
// refuses.
export interface SendPreconditions {
  subject?: string
  body?: string
  /** Checked recipients — `entityCheckboxContainer.checkedEntities`. */
  recipientCount?: number
  /**
   * ⚠️ NOT THE SAME AS `recipientCount`. `validAndAvailableEntitiesToSend` is the subset the
   * server will actually accept — a selection of twelve where all twelve are blacklisted or
   * have no address is `recipientCount: 12` and `sendableCount: 0`, and upstream refuses it.
   */
  sendableCount?: number
  /** `remainingDailyEntities` — the per-user daily cap. */
  remainingDaily?: number
  /** `invalidParameters` — unresolved `{{placeholders}}` in the subject or body. */
  invalidParameterCount?: number
  /** `isEditingFollowup` — a half-written follow-up blocks the primary send. */
  editingFollowup?: boolean
  /**
   * `hasUnsavedSignature` — the signature editor is open with edits that differ from the saved
   * signature (upstream woo@10e2fe0302, HubSpot 49214431098).
   *
   * ⚠️ IT IS ON `checkCanSendMessage`, NOT ON `checkIsMessageDataValid`. An unsaved signature
   * blocks the SEND and leaves "Save as new template" alone, which is upstream's placement: the
   * message itself is fine, only what would go out under it is undecided. Sending would mail
   * the stored signature while the screen shows a different one.
   */
  unsavedSignature?: boolean
}

/** `checkIsMessageDataValid` — the message itself, independent of who it goes to. */
export function isMessageDataValid(state: SendPreconditions): boolean {
  return (
    (state.subject ?? '').length > 0 &&
    (state.body ?? '').length > 0 &&
    (state.invalidParameterCount ?? 0) === 0 &&
    state.editingFollowup !== true
  )
}

/**
 * `checkCanSendMessage` — the message, the recipients, and the cap.
 *
 * ⚠️ `remainingDaily > 0` IS STRICT. Zero remaining is not "send the last one"; it is the cap
 * reached, and the composer shows its own notice in that state.
 */
export function canSendMessage(state: SendPreconditions): boolean {
  return (
    isMessageDataValid(state) &&
    (state.remainingDaily ?? 0) > 0 &&
    (state.recipientCount ?? 0) > 0 &&
    (state.sendableCount ?? 0) > 0 &&
    state.unsavedSignature !== true
  )
}

/** `getDailySendLimitCopy()` — verbatim, straight apostrophe and all. */
export const DAILY_SEND_LIMIT_COPY = "You've reached your daily sending limit. It resets tomorrow."

/**
 * `isDailySendLimitReached()` — `remainingDailyEntities <= 0 && checkedEntities.length > 0`.
 *
 * ⚠️ AN UNFETCHED CAP IS NOT A REACHED ONE. Upstream's field is `undefined` until
 * `getRemainingDailyMessagesForUser` answers, and `undefined <= 0` is false — so while the
 * composer loads, the tooltip asks for recipients, subject and body rather than announcing a
 * limit nobody has hit. `canSendMessage` defaults the same absence to 0, which is the other
 * half of that: the button is inert, the copy is not alarming.
 */
export function isDailySendLimitReached(state: SendPreconditions): boolean {
  return state.remainingDaily !== undefined && state.remainingDaily <= 0 && (state.recipientCount ?? 0) > 0
}

/**
 * `calculateDisabledSendButtonCopy()` (`extension-alert-entity-messages-controller.ng1.ts:622`),
 * ported clause for clause — the `uib-tooltip` on the faded Send.
 *
 * ⚠️ THE CLAUSES ACCUMULATE, THEY DO NOT REPLACE. The first two are exclusive (the daily cap
 * must precede the generic copy, because the cap empties `validAndAvailableEntitiesToSend`
 * and would otherwise read as "add recipients"); the follow-up and signature clauses are then
 * APPENDED, space-separated, to whatever is already there. A composer with no subject and an
 * unsaved signature says both.
 *
 * ⚠️ AND IT CAN ANSWER `undefined` WHILE SEND IS STILL DISABLED — an unresolved
 * `{{placeholder}}` has no clause here (the footer's invalid-parameter label says it instead).
 * uib does not open a tooltip with no content, so the button is simply silent then.
 */
export function disabledSendButtonCopy(state: SendPreconditions): string | undefined {
  let copy: string | undefined
  if (isDailySendLimitReached(state)) {
    copy = DAILY_SEND_LIMIT_COPY
  } else if ((state.subject ?? '') === '' || (state.body ?? '') === '' || (state.sendableCount ?? 0) === 0) {
    copy = 'Please make sure to add recipients, subject and body.'
  }
  if (state.editingFollowup === true) {
    copy = `${copy === undefined ? '' : `${copy} `}Save or close follow-up email editor to continue.`
  }
  if (state.unsavedSignature === true) {
    copy = `${copy === undefined ? '' : `${copy} `}Save or cancel your signature changes to continue.`
  }
  return copy
}
